import { lioboCustomer } from '@/features/customers/api/lioboCustomer.ts';
import { SubscriptionActionService } from '@/features/subscriptions';
import { usePermissions } from '@/shared/hooks/usePermissions.ts';
import { USER_PERMISSIONS } from '@/shared/types/permissions.ts';
import { CAlert, CBadge, CButton, CSpinner } from '@coreui/react';
import React from 'react';
import type { Customer } from '../../types';

interface CustomerDetailsProps {
  customer: Customer;
  onRefetch: () => void;
  onShowSuccess?: (message: string) => void;
}

export const CustomerDetails: React.FC<CustomerDetailsProps> = ({
  customer,
  onRefetch,
  onShowSuccess,
}) => {
  const { hasPermission } = usePermissions();
  const canLiobo = hasPermission(USER_PERMISSIONS.Liobo);
  const canChangePurrscribeAccountStatus = hasPermission(
    USER_PERMISSIONS.CustomersAccountStatusChangePurrscribeAccountStatus,
  );
  const [isSubmittingLockAction, setIsSubmittingLockAction] = React.useState(false);
  const [lockActionError, setLockActionError] = React.useState<string | null>(null);

  const canLockAccount =
    Boolean(customer.subscriptionPlan) &&
    !customer.subscriptionPlan?.isLocked &&
    customer.subscriptionPlan?.status?.toLowerCase() !== 'active';
  const canUnlockAccount =
    Boolean(customer.subscriptionPlan) && customer.subscriptionPlan?.isLocked;

  const handleLockToggle = async () => {
    if (!customer.subscriptionPlan) {
      return;
    }
    const wasLocked = customer.subscriptionPlan.isLocked;
    setLockActionError(null);
    setIsSubmittingLockAction(true);
    try {
      if (customer.subscriptionPlan.isLocked) {
        await SubscriptionActionService.unlock(customer.shopifyCustomerId);
      } else {
        await SubscriptionActionService.lock(customer.shopifyCustomerId);
      }
      onShowSuccess?.(wasLocked ? 'Account unlocked' : 'Account locked');
      onRefetch();
    } catch (error) {
      setLockActionError(
        error instanceof Error ? error.message : 'Failed to update account status',
      );
    } finally {
      setIsSubmittingLockAction(false);
    }
  };

  return (
    <div>
      <div className="mb-3">
        <h3>Customer Details</h3>
      </div>
      <div className="mb-3">
        <strong>Shopify ID:</strong> {customer.shopifyCustomerId}
      </div>
      <div className="mb-3">
        <strong>Name:</strong> {customer.fullName}
      </div>
      <div className="mb-3">
        <strong>Email:</strong> {customer.email}
      </div>
      {customer.subscriptionPlan && (
        <div className="mb-3">
          <strong>Purrscribe Account Status:</strong>{' '}
          <CBadge color={customer.subscriptionPlan.isLocked ? 'danger' : 'success'}>
            {customer.subscriptionPlan.isLocked ? 'LOCKED' : 'UNLOCKED'}
          </CBadge>
          {canChangePurrscribeAccountStatus && (
            <span className="ms-3">
              <CButton
                type="button"
                color="link"
                className="p-0 align-baseline"
                disabled={isSubmittingLockAction || (!canLockAccount && !canUnlockAccount)}
                onClick={(...args: Parameters<typeof handleLockToggle>) =>
                  void handleLockToggle(...args)
                }
              >
                {isSubmittingLockAction ? (
                  <CSpinner size="sm" />
                ) : customer.subscriptionPlan.isLocked ? (
                  'Unlock Account'
                ) : (
                  'Lock Account'
                )}
              </CButton>
            </span>
          )}
        </div>
      )}
      {lockActionError && (
        <CAlert color="danger" className="mb-3">
          {lockActionError}
        </CAlert>
      )}
      {customer.subscriptionPlan && canLiobo && (
        <CButton
          type="button"
          color="primary"
          variant="outline"
          disabled={customer.subscriptionPlan.isLocked}
          onClick={() => void lioboCustomer(customer.shopifyCustomerId.toString())}
        >
          Log in as a Customer
        </CButton>
      )}
    </div>
  );
};
